import type { BillingResidency } from '@alpod-ai-writer/shared';
import { useQuery } from '@tanstack/react-query';
import { CurrencyCode, encode, PaymentOptions } from 'bysquare/pay';
import { Button, Card, Spinner } from 'flowbite-react';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate, useParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { useModals } from '../../../hooks/useModals.hook';
import { getCurrencyFormatter } from '../../../lib/format.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { useSession } from '../../hooks/useSession.hook';
import { PaymentCheckModal } from '../../modals/payment/paymentCheckModal.component';
import { RouteIndex } from '../../routeIndex';

const DocumentBankTransferPaymentView = () => {
    const navigate = useNavigate();
    const api = useApi();
    const modals = useModals();
    const { identity } = useSession();
    const { documentId } = useParams<{ documentId: string }>();

    const billingResidency = identity.userFields['billingResidency'] as BillingResidency | undefined;

    const editPath = useMemo(
        () => pathWithParams(RouteIndex.documentDetail.edit, { documentId: documentId ?? '' }),
        [documentId]
    );

    const documentQuery = useQuery({
        queryKey: ['document', documentId],
        queryFn: async () => {
            return api.v1.documents.getById(documentId ?? '');
        },
        enabled: !!documentId,
    });

    const configQuery = useQuery({
        queryKey: ['config', 'payment'],
        queryFn: async () => {
            return api.v1.config.getPaymentConfig();
        },
    });

    const unlockSettingsQuery = useQuery({
        queryKey: ['config', 'document-unlock-settings'],
        queryFn: () => api.v1.config.getDocumentUnlockSettings(),
    });

    // Ensures a PENDING payment order exists (or updates it if residency changed).
    useQuery({
        queryKey: ['document', documentId, 'bankTransferOrder', billingResidency],
        queryFn: () => api.v1.documents.getBankTransferQr(documentId!, billingResidency!),
        enabled: !!documentId && !!billingResidency,
    });

    const paymentConfig = billingResidency && configQuery.data ? configQuery.data[billingResidency] : undefined;
    const currencyFormatter = getCurrencyFormatter(paymentConfig?.currency ?? 'CZK', 'cs-CZ');

    const variableSymbol = documentQuery.data?.variableSymbol ?? '';
    const documentName = documentQuery.data?.documentName ?? '';
    const finalPrice = billingResidency === 'SK'
        ? (unlockSettingsQuery.data?.priceEUR ?? paymentConfig?.price ?? 0)
        : (unlockSettingsQuery.data?.priceCZK ?? paymentConfig?.price ?? 0);

    useEffect(() => {
        if (!billingResidency || !documentId) {
            navigate(editPath, { replace: true });
        }
    }, [billingResidency, documentId, navigate, editPath]);

    useEffect(() => {
        if (documentQuery.data?.paidForAt) {
            navigate(editPath, { replace: true });
        }
    }, [documentQuery.data?.paidForAt, navigate, editPath]);

    useEffect(() => {
        if (paymentConfig && !paymentConfig.bankTransfer) {
            navigate(editPath, { replace: true });
        }
    }, [paymentConfig, navigate, editPath]);

    const spdString = useMemo(() => {
        if (!paymentConfig?.bankTransfer || billingResidency !== 'CZ' || !variableSymbol) return '';
        const { bankTransfer, currency } = paymentConfig;
        const dueDateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        return `SPD*1.0*ACC:${bankTransfer.iban}*AM:${finalPrice.toFixed(2)}*CC:${currency}*MSG:${variableSymbol}*X-VS:${variableSymbol}*RN:EWMITS s.r.o.*DT:${dueDateStr}*PT:IP`;
    }, [paymentConfig, billingResidency, variableSymbol, finalPrice]);

    const qrValue = useMemo(() => {
        if (!paymentConfig?.bankTransfer || !variableSymbol) return spdString;
        if (billingResidency !== 'SK') return spdString;

        const dueDateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        return encode({
            payments: [{
                type: PaymentOptions.PaymentOrder,
                amount: finalPrice,
                variableSymbol,
                currencyCode: CurrencyCode.EUR,
                beneficiary: { name: 'Dyarvi s.r.o.' },
                bankAccounts: [{ iban: paymentConfig.bankTransfer.iban }],
                paymentDueDate: dueDateStr,
            }],
        }, { version: 0 });
    }, [paymentConfig, billingResidency, variableSymbol, finalPrice, spdString]);

    const handlePaidClick = useCallback(() => {
        if (!documentId) return;
        modals.showModal('document-payment-check', PaymentCheckModal, { documentId });
    }, [modals, documentId]);

    const handleBack = useCallback(() => {
        navigate(editPath);
    }, [navigate, editPath]);

    if (!documentId || !billingResidency) {
        return null;
    }

    const isLoading = documentQuery.isLoading || configQuery.isLoading;

    return (
        <>
            <AppHeader
                pageTitle='Platba bankovním převodem'
                backLink={editPath}
            />

            <div className='p-4 pt-16 md:pt-20 md:pb-12'>
                <div className='max-w-7xl mx-auto'>
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                        {/* Bank transfer payment data */}
                        <div className='lg:col-span-2'>
                            <Card>
                                <h2 className='text-2xl font-bold text-gray-900 mb-4'>
                                    Platební údaje
                                </h2>

                                {isLoading && (
                                    <div className='flex justify-center py-8'>
                                        <Spinner size='xl' />
                                    </div>
                                )}

                                {paymentConfig?.bankTransfer && variableSymbol && (
                                    <>
                                        <div className='flex flex-col md:flex-row gap-6 md:items-center mb-4 md:mb-2'>
                                            <div className='bg-white p-4 rounded-lg w-auto'>
                                                <QRCode value={qrValue} size={230} className='w-full md:w-auto' />
                                            </div>
                                            <div className='flex flex-col items-start gap-2'>
                                                <div>
                                                    <span className='inline-block w-[9rem]'>Název účtu:</span>
                                                    <span className='font-semibold'>{billingResidency === 'SK' ? 'Dyarvi s.r.o.' : 'EWMITS s.r.o.'}</span>
                                                </div>
                                                <div>
                                                    <span className='inline-block w-[9rem]'>Číslo účtu:</span>
                                                    <span className='font-semibold'>{paymentConfig.bankTransfer.accountNumber}</span>
                                                </div>
                                                <div>
                                                    <span className='inline-block w-[9rem]'>IBAN:</span>
                                                    <span className='font-semibold'>{paymentConfig.bankTransfer.iban}</span>
                                                </div>
                                                <div>
                                                    <span className='inline-block w-[9rem]'>Částka k platbě:</span>
                                                    <span className='font-semibold'>{currencyFormatter.format(finalPrice)}</span>
                                                </div>
                                                <div>
                                                    <div>
                                                        <span className='inline-block w-[9rem]'>Variabilní symbol:</span>
                                                        <span className='font-semibold'>{variableSymbol}</span>
                                                    </div>
                                                    <p className='text-sm text-gray-700 mt-2'>Uveďte prosím správný variabilní symbol, jinak Vaše platba nebude automaticky spárována s tímto dokumentem.</p>
                                                </div>

                                                <p className='text-base font-bold mt-1'>Platbu proveďte okamžitým převodem.</p>

                                                <Button
                                                    color='primary'
                                                    size='lg'
                                                    onClick={handlePaidClick}
                                                >
                                                    <CheckIcon size={24} className='mr-2' />
                                                    <span>Mám zaplaceno</span>
                                                </Button>
                                            </div>
                                        </div>

                                        <div className='border-t pt-4'>
                                            <p className='text-base text-gray-700 mb-4 font-medium'>
                                                Pokud platbu provedete okamžitým převodem, bude zpracována do 10ti minut a všechny funkce Vám budou poté ihned odemčeny.
                                                V opačném případě může zpracování platby trvat až 24 hodin.
                                            </p>
                                            <p className='text-base text-gray-700'>
                                                Pokud platba trvá déle nebo jste platbu poslali se špatným variabilním symbolem, kontaktujte nás na <a className='hyperlink' href="mailto:info@aikorektury.cz">info@aikorektury.cz</a>.
                                            </p>
                                        </div>
                                    </>
                                )}

                                <div className='flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200'>
                                    <Button
                                        type='button'
                                        color='light'
                                        size='lg'
                                        onClick={handleBack}
                                    >
                                        <ArrowLeftIcon className='mr-2 h-5 w-5' />
                                        Zpět na dokument
                                    </Button>
                                </div>
                            </Card>
                        </div>

                        {/* Order Summary Section */}
                        <div className='lg:col-span-1'>
                            <div className='lg:sticky lg:top-20'>
                                <Card>
                                    <h2 className='text-xl font-bold text-gray-900 mb-4'>
                                        Souhrn objednávky
                                    </h2>

                                    <div className='flex flex-col gap-4'>
                                        <div className='flex justify-between items-start pb-3 border-b border-gray-200'>
                                            <div className='flex-1'>
                                                <p className='font-medium text-gray-900'>Odemčení dokumentu</p>
                                                {documentName && (
                                                    <p className='text-sm text-gray-500 break-words'>
                                                        {documentName}
                                                    </p>
                                                )}
                                            </div>
                                            <div className='text-right'>
                                                <p className='font-semibold text-gray-900'>
                                                    {currencyFormatter.format(finalPrice)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='pt-3 border-t-2 border-gray-300'>
                                            <div className='flex justify-between items-center mb-2'>
                                                <span className='text-gray-700'>Variabilní symbol:</span>
                                                <span className='font-semibold text-gray-900'>
                                                    {variableSymbol}
                                                </span>
                                            </div>

                                            <div className='flex justify-between items-center pt-2 border-t border-gray-200'>
                                                <span className='text-lg font-semibold text-gray-900'>Celková cena:</span>
                                                <span className='text-2xl font-bold text-gray-900'>
                                                    {currencyFormatter.format(finalPrice)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DocumentBankTransferPaymentView;
